import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import DateInput from './DateInput.jsx';

function contactLabel(c) {
  const name = [c.first_name, c.last_name].filter(Boolean).join(' ');
  const primary = c.company || name || `Contact #${c.id}`;
  const tag = c.record_type === 'lead' ? 'Lead'
            : c.record_type === 'customer' ? 'Customer'
            : c.record_type === 'distributor' ? 'Distributor'
            : c.record_type === 'strategic_account' ? 'Strategic'
            : c.record_type === 'vendor_contact' ? 'Vendor'
            : '';
  return { primary, secondary: c.company && name ? name : '', tag };
}

const NOTE_TYPES = ['Phone', 'Email', 'Meeting', 'Demo', 'Other'];

export default function QuickNoteModal({ open, onClose, onSaved }) {
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [search, setSearch] = useState('');
  const [contactId, setContactId] = useState(null);
  const [type, setType] = useState('Phone');
  const [text, setText] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedToast, setSavedToast] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setContactId(null);
    setType('Phone');
    setText('');
    setDate(new Date().toISOString().slice(0, 10));
    setError(null);
    setSavedToast(false);
    setLoadingContacts(true);
    axios.get('/api/contacts')
      .then((res) => setContacts(res.data.contacts || []))
      .catch((err) => setError(err.response?.data?.error || err.message))
      .finally(() => setLoadingContacts(false));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contacts.slice(0, 80);
    return contacts.filter((c) => {
      const haystack = [c.company, c.first_name, c.last_name, c.record_type]
        .filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    }).slice(0, 80);
  }, [contacts, search]);

  const selected = useMemo(
    () => contacts.find((c) => c.id === contactId) || null,
    [contacts, contactId]
  );

  if (!open) return null;

  async function handleSave(e) {
    e.preventDefault();
    setError(null);
    if (!contactId) return setError('Select a contact first.');
    if (!text.trim()) return setError('Note text is required.');
    setSaving(true);
    try {
      await axios.post('/api/notes', { contact_id: contactId, note_date: date, type, text });
      setSavedToast(true);
      if (onSaved) onSaved();
      setTimeout(() => onClose(), 700);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={(e) => {
      if (e.target === e.currentTarget && !saving) onClose();
    }}>
      <div className="modal-card" role="dialog" aria-modal="true" aria-label="Quick Note">
        <header className="modal-head">
          <h3>Log a Note</h3>
          <button type="button" className="modal-close" onClick={onClose} disabled={saving} aria-label="Close">×</button>
        </header>
        <form className="modal-body" onSubmit={handleSave}>
          {savedToast && <p className="save-message">Saved.</p>}
          {error && <p className="error">{error}</p>}

          <label className="form-field">
            <span>Contact</span>
            {selected ? (
              <div className="quicknote-selected">
                <div>
                  <div className="quicknote-selected-primary">{contactLabel(selected).primary}</div>
                  {contactLabel(selected).secondary && (
                    <div className="quicknote-selected-secondary">{contactLabel(selected).secondary}</div>
                  )}
                </div>
                <button type="button" className="btn-secondary" onClick={() => setContactId(null)}>
                  Change
                </button>
              </div>
            ) : (
              <>
                <input
                  type="search"
                  placeholder={loadingContacts ? 'Loading…' : 'Search company or name…'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                  disabled={loadingContacts}
                />
                {!loadingContacts && (
                  <ul className="quicknote-list">
                    {filtered.length === 0 && (
                      <li className="quicknote-empty">No matches.</li>
                    )}
                    {filtered.map((c) => {
                      const { primary, secondary, tag } = contactLabel(c);
                      return (
                        <li
                          key={c.id}
                          className="quicknote-option"
                          onClick={() => setContactId(c.id)}
                        >
                          <div className="quicknote-option-main">
                            <div className="quicknote-option-primary">{primary}</div>
                            {secondary && <div className="quicknote-option-secondary">{secondary}</div>}
                          </div>
                          <span className="quicknote-option-tag">{tag}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            )}
          </label>

          <div className="form-grid" style={{ marginTop: 'var(--space-3)' }}>
            <label className="form-field">
              <span>Type</span>
              <select value={type} onChange={(e) => setType(e.target.value)}>
                {NOTE_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </label>
            <label className="form-field">
              <span>Date</span>
              <DateInput value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
          </div>

          <label className="form-field" style={{ marginTop: 'var(--space-3)' }}>
            <span>Note</span>
            <textarea
              rows={4}
              placeholder="What happened on the call / email / meeting?"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </label>

          <div className="form-actions" style={{ marginTop: 'var(--space-4)' }}>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving || !contactId}>
              {saving ? 'Saving…' : 'Save Note'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
